import { Component, inject, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { MenuController, ModalController } from '@ionic/angular/standalone';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Subscription, filter, switchMap, take } from 'rxjs';
import { IonApp, IonSplitPane, IonMenu, IonHeader, IonToolbar, IonTitle,
         IonContent, IonList, IonItem, IonIcon, IonLabel, IonRouterOutlet, IonButton, IonMenuToggle } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { briefcaseOutline, documentTextOutline, cubeOutline, personOutline, menuOutline, addOutline } from 'ionicons/icons';
import { ClerkService } from 'ngx-clerk-iliad';
import { environment } from '../environments/environment';
import { UserService } from './services/user.service';
import { RoleService } from './services/role.service';
import { ThemeService } from './services/theme.service';
import { User } from './models/user.model';

// Add the icons we're using
addIcons({
  'briefcase': briefcaseOutline,
  'document-text': documentTextOutline,
  'cube': cubeOutline,
  'person': personOutline,
  'menu-outline': menuOutline,
  'add': addOutline
});

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    IonApp,
    IonSplitPane,
    IonMenu,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonList,
    IonItem,
    IonIcon,
    IonLabel,
    IonRouterOutlet,
    IonButton,
    IonMenuToggle
  ]
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  // Common pages for all users
  commonPages = [
    { title: 'Account', url: '/account', icon: 'person' }
  ];

  // Pages specific to creator users
  creatorPages = [
    { title: 'Projects', url: '/projects', icon: 'briefcase' },
    { title: 'Applications', url: '/applications', icon: 'document-text' },
    { title: 'Deliveries', url: '/deliveries', icon: 'cube' }
  ];

  // Pages specific to business users
  businessPages = [
    { title: 'Projects', url: '/projects', icon: 'briefcase' },
    { title: 'Applications', url: '/applications', icon: 'document-text' },
    { title: 'Deliveries', url: '/deliveries', icon: 'cube' }
  ];

  // Combined pages based on user role
  appPages: any[] = [];

  isAuthRoute = false;
  isAuthenticated = false;
  isCreator = false;
  isBusiness = false;
  isDarkMode = false;
  userName = '';

  // Build number from environment
  buildNumber = environment.buildNumber;

  // References to logo elements for cleanup
  private darkLogoElement: HTMLImageElement | null = null;
  private lightLogoElement: HTMLImageElement | null = null;

  // References for cleanup of observers and timeouts
  private logoObserver: MutationObserver | null = null;
  private logoObserverTimeout: number | null = null;

  private routerSubscription!: Subscription;
  private authSubscription!: Subscription;
  private roleSubscription!: Subscription;
  private themeSubscription!: Subscription;

  private router = inject(Router);
  private menu = inject(MenuController);
  private modalCtrl = inject(ModalController);
  private _clerk = inject(ClerkService);
  private userService = inject(UserService);
  private roleService = inject(RoleService);
  private themeService = inject(ThemeService);

  constructor() {
    // Pin Clerk JS to v5 UI bundle to avoid breaking changes from @latest
    this._clerk.__init({ publishableKey: environment.clerkPublishableKey, clerkJSUrl: 'https://cdn.jsdelivr.net/npm/@clerk/clerk-js@5/dist/clerk.browser.js' });

    // Initialize isAuthRoute based on the current URL
    this.isAuthRoute = this.router.url.includes('/auth');
    console.log('Initial URL:', this.router.url);
    console.log('Initial isAuthRoute:', this.isAuthRoute);
    console.log('[AppComponent] Build:', this.buildNumber, 'Production:', environment.production, 'API:', environment.apiUrl);
  }

  async openFeedback() {
    const { FeedbackModalComponent } = await import('./components/feedback-modal/feedback-modal.component');
    const modal = await this.modalCtrl.create({ component: FeedbackModalComponent });
    await modal.present();
  }

  private resolveUserName(user: User | null | undefined): string {
    if (!user) return '';
    return (
      user.full_legal_name?.trim() ||
      user.name?.trim() ||
      [user.first_name, user.last_name].filter(Boolean).join(' ').trim() ||
      user.profile_username?.trim() ||
      user.email ||
      ''
    );
  }

  ngOnInit() {
    // Subscribe to router events to track auth routes
    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      // Check if the current URL includes '/auth'
      this.isAuthRoute = event.url.includes('/auth');
      console.log('Current URL:', event.url);
      console.log('isAuthRoute:', this.isAuthRoute);
    });

    // Subscribe to authentication state and handle role-based redirection
    this.authSubscription = this._clerk.user$.subscribe(user => {
      const wasAuthenticated = this.isAuthenticated;
      this.isAuthenticated = !!user;

      console.log('[AppComponent] Auth state changed:', { wasAuthenticated, isAuthenticated: this.isAuthenticated });

      // If user just became authenticated
      if (!wasAuthenticated && this.isAuthenticated) {
        // Check if the user is a creator with an incomplete profile
        this.userService.isCreatorWithIncompleteProfile().pipe(take(1)).subscribe(isIncomplete => {
          if (isIncomplete) {
            // If creator with incomplete profile, route to projects and let the guard redirect safely
            console.log('Routing to /projects; profile-completion guard will redirect to creator profile if needed');
            this.router.navigate(['/projects'], { replaceUrl: true });
          } else if (this.isAuthRoute) {
            // Fetch user profile to get role information
            this.userService.getCurrentUser().pipe(take(1)).subscribe((user: User | null) => {
              this.userName = this.resolveUserName(user);
              if (user && user.roles) {
                // Update role flags and navigation menu immediately
                this.isCreator = user.roles.includes('creator');
                this.isBusiness = user.roles.includes('business');
                this.updateNavigationMenu();
                console.log('Updated navigation menu after login with roles:', user.roles);

                // Role-based redirection
                console.log('Redirecting authenticated user to projects');
                this.router.navigate(['/projects'], { replaceUrl: true });
              } else {
                // Fallback if no user or roles
                console.log('No user roles found, redirecting to projects');
                this.router.navigate(['/projects'], { replaceUrl: true });
              }
            });
          }
        });
      }
    });

    // Subscribe to role service to update navigation menu based on user role
    this.roleSubscription = this.roleService.getCurrentUser().subscribe(user => {
      this.userName = this.resolveUserName(user);
      if (user && user.roles) {
        // Update role flags
        this.isCreator = user.roles.includes('creator');
        this.isBusiness = user.roles.includes('business');

        // Update navigation menu based on role
        this.updateNavigationMenu();
      }
    });

    // Subscribe to theme changes to ensure proper logo display
    this.themeSubscription = this.themeService.darkMode$.subscribe(isDark => {
      console.log('Theme changed, dark mode:', isDark);
      console.log('Body classes:', document.body.classList.toString());

      // Update component property to track theme state
      this.isDarkMode = isDark;

      // Update logo visibility based on current theme
      this.updateLogoVisibility(isDark);
    });
  }

  ngAfterViewInit() {
    console.log('AppComponent view initialized');

    // Get the current theme state
    // Note: We need to access the current value of the BehaviorSubject
    // This is a workaround since we can't directly access the private property
    let currentTheme = false;
    this.themeService.darkMode$.subscribe(isDark => {
      currentTheme = isDark;
    }).unsubscribe(); // Immediately unsubscribe to avoid memory leaks

    console.log('Initial theme on view init:', currentTheme ? 'dark' : 'light');

    // Update logo visibility based on initial theme
    this.updateLogoVisibility(currentTheme);
  }

  ngOnDestroy() {
    // Unsubscribe from all subscriptions
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
    if (this.roleSubscription) {
      this.roleSubscription.unsubscribe();
    }
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe();
    }

    // Clean up event handlers on logo elements
    if (this.darkLogoElement) {
      this.darkLogoElement.onload = null;
      this.darkLogoElement.onerror = null;
      this.darkLogoElement = null;
    }

    if (this.lightLogoElement) {
      this.lightLogoElement.onload = null;
      this.lightLogoElement.onerror = null;
      this.lightLogoElement = null;
    }

    // Clean up any active logo observer and timeout
    this.cleanupLogoObserver();

    console.log('AppComponent destroyed, all resources cleaned up');
  }

  /**
   * Updates logo visibility based on the current theme
   * @param isDark Whether dark mode is active
   */
  private updateLogoVisibility(isDark: boolean) {
    console.log('Updating logo visibility, dark mode:', isDark);

    // Skip logo update only if we're on an auth route (auth page handles its own logo)
    // but allow updates for navbar logo regardless of authentication state
    if (this.isAuthRoute) {
      console.log('Skipping logo visibility update - on auth route which handles its own logo');
      return;
    }

    // Use MutationObserver to detect when logo elements are available in the DOM
    this.waitForLogoElements().then(([darkLogo, lightLogo]) => {
      console.log('Found logo elements, updating visibility directly');

      // Double-check body classes to ensure we're using the correct theme state
      const bodyClasses = document.body.classList;
      console.log('Current body classes:', bodyClasses.toString());

      // If body has explicit theme classes, use those instead of the parameter
      let effectiveTheme = isDark;
      if (bodyClasses.contains('dark')) {
        console.log('Body has dark class, overriding theme parameter');
        effectiveTheme = true;
      } else if (bodyClasses.contains('light')) {
        console.log('Body has light class, overriding theme parameter');
        effectiveTheme = false;
      } else {
        console.log('No theme classes found on body, using provided theme parameter:', isDark);
      }

      // Force reload images by updating src with current timestamp
      const timestamp = new Date().getTime();
      const darkSvgPath = '../assets/images/ccn-logo-dark.svg';
      const lightSvgPath = '../assets/images/ccn-logo-light.svg';

      // Set SVG paths with timestamp
      const darkSrc = darkSvgPath + '?v=' + timestamp;
      const lightSrc = lightSvgPath + '?v=' + timestamp;

      console.log('Setting image sources with timestamp:', timestamp);
      console.log('Dark logo src:', darkSrc);
      console.log('Light logo src:', lightSrc);

      // Set visibility based on effective theme
      if (effectiveTheme) {
        console.log('Using dark theme, showing dark logo');
        darkLogo.style.display = 'block';
        lightLogo.style.display = 'none';
      } else {
        console.log('Using light theme, showing light logo');
        darkLogo.style.display = 'none';
        lightLogo.style.display = 'block';
      }

      // Store references to logo elements for cleanup
      this.darkLogoElement = darkLogo;
      this.lightLogoElement = lightLogo;

      // Add load event listeners to verify images are loading
      if (this.darkLogoElement) {
        this.darkLogoElement.onload = () => console.log('Dark SVG logo loaded successfully');
        this.darkLogoElement.onerror = () => console.error('Failed to load dark SVG logo');
      }

      if (this.lightLogoElement) {
        this.lightLogoElement.onload = () => console.log('Light SVG logo loaded successfully');
        this.lightLogoElement.onerror = () => console.error('Failed to load light SVG logo');
      }

      // Set new src to force reload
      darkLogo.src = darkSrc;
      lightLogo.src = lightSrc;

      console.log('Updated logo visibility - Dark logo:', darkLogo.style.display, 'Light logo:', lightLogo.style.display);
    }).catch(error => {
      console.error('Failed to find logo elements:', error);
    });
  }

  /**
   * Waits for logo elements to be available in the DOM
   * @returns Promise that resolves with the logo elements when they're available
   */
  private waitForLogoElements(): Promise<[HTMLImageElement, HTMLImageElement]> {
    return new Promise((resolve, reject) => {
      // Clean up any existing observer and timeout
      this.cleanupLogoObserver();

      // First, check if elements already exist
      const darkLogo = document.querySelector('.logo-dark') as HTMLImageElement;
      const lightLogo = document.querySelector('.logo-light') as HTMLImageElement;

      if (darkLogo && lightLogo) {
        console.log('Logo elements already in DOM');
        resolve([darkLogo, lightLogo]);
        return;
      }

      console.log('Logo elements not found, setting up observer');

      // Set up a MutationObserver to watch for changes to the DOM
      this.logoObserver = new MutationObserver((mutations, obs) => {
        const darkLogo = document.querySelector('.logo-dark') as HTMLImageElement;
        const lightLogo = document.querySelector('.logo-light') as HTMLImageElement;

        if (darkLogo && lightLogo) {
          console.log('Logo elements found in DOM via observer');
          this.cleanupLogoObserver(); // Clean up observer and timeout
          resolve([darkLogo, lightLogo]);
        }
      });

      // Start observing with a configuration to watch for child list changes
      this.logoObserver.observe(document.body, {
        childList: true,
        subtree: true
      });

      // Set a timeout to avoid waiting indefinitely
      this.logoObserverTimeout = window.setTimeout(() => {
        console.log('Timed out waiting for logo elements');
        this.cleanupLogoObserver();
        reject(new Error('Timed out waiting for logo elements'));
      }, 5000); // 5 second timeout
    });
  }

  /**
   * Cleans up any active logo observer and timeout
   */
  private cleanupLogoObserver(): void {
    // Disconnect observer if it exists
    if (this.logoObserver) {
      this.logoObserver.disconnect();
      this.logoObserver = null;
      console.log('Logo observer disconnected');
    }

    // Clear timeout if it exists
    if (this.logoObserverTimeout !== null) {
      window.clearTimeout(this.logoObserverTimeout);
      this.logoObserverTimeout = null;
      console.log('Logo observer timeout cleared');
    }
  }

  /**
   * Update the navigation menu based on the user's role
   */
  updateNavigationMenu() {
    // Start with role-specific pages
    this.appPages = [];

    // Add role-specific pages
    if (this.isCreator) {
      this.appPages = [...this.creatorPages];
    } else if (this.isBusiness) {
      this.appPages = [...this.businessPages];
    }

    // Add common pages at the end
    this.appPages = [...this.appPages, ...this.commonPages];

    console.log('Updated navigation menu for role:', this.isCreator ? 'creator' : this.isBusiness ? 'business' : 'unknown');
  }

  navigateTo(url: string) {
    this.router.navigateByUrl(url);
    this.menu.close();
  }
}
